import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import {
  Users, BookOpen, ClipboardList, ClipboardCheck, CalendarCheck,
  Bell, ArrowRight, Sparkles, Megaphone, Bot, Wand2, ScrollText, FileText,
  BarChart3, PenTool, Heart, FolderOpen, MessageSquare, CalendarDays, Video,
} from "lucide-react";
import { AICallout } from "@/components/makai/primitives";
import { WeeklySummaryCard } from "@/components/intelligence/weekly-summary-card";

export const Route = createFileRoute("/teacher/")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Teacher — MAKAI" }] }),
  component: TeacherHome,
});

function TeacherHome() {
  const ctx = useTeacherContext();
  const [counts, setCounts] = useState({ assignmentsDue: 0, ungraded: 0, attToday: 0, attTotal: 0, learners: 0 });
  const [recentAssignments, setRecentAssignments] = useState<any[]>([]);
  const [recentSubmissions, setRecentSubmissions] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [todaySlots, setTodaySlots] = useState<any[]>([]);

  useEffect(() => {
    if (ctx.loading || !ctx.staffId || !ctx.schoolId) return;
    const staffId = ctx.staffId;
    const streamIds = ctx.streams.map((s) => s.id);
    const today = new Date().toISOString().slice(0, 10);
    const weekAhead = new Date(Date.now() + 7 * 86400_000).toISOString();

    const dayName = (["sun","mon","tue","wed","thu","fri","sat"] as const)[new Date().getDay()];

    (async () => {
      const [assignsDue, ungraded, attToday, learnerCount, recA, recS, anns, slots] = await Promise.all([
        streamIds.length ? supabase.from("assignments")
          .select("id", { head: true, count: "exact" })
          .eq("teacher_id", staffId)
          .gte("due_at", new Date().toISOString()).lte("due_at", weekAhead) : Promise.resolve({ count: 0 } as any),
        supabase.from("assignment_submissions")
          .select("id, assignment:assignments!inner(teacher_id)", { head: true, count: "exact" })
          .eq("assignment.teacher_id", staffId)
          .in("status", ["submitted", "late"]),
        streamIds.length ? supabase.from("attendance")
          .select("status").in("stream_id", streamIds).eq("date", today) : Promise.resolve({ data: [] } as any),
        streamIds.length ? supabase.from("learners")
          .select("id", { head: true, count: "exact" })
          .in("current_stream_id", streamIds).eq("status", "active") : Promise.resolve({ count: 0 } as any),
        supabase.from("assignments")
          .select("id,title,due_at,stream:streams(name,class:classes(name)),subject:subjects_catalog(name)")
          .eq("teacher_id", staffId).order("created_at", { ascending: false }).limit(5),
        supabase.from("assignment_submissions")
          .select("id,status,submitted_at,score,learner:learners(first_name,last_name),assignment:assignments!inner(title,teacher_id)")
          .eq("assignment.teacher_id", staffId).order("submitted_at", { ascending: false, nullsFirst: false }).limit(5),
        streamIds.length ? supabase.from("announcements")
          .select("id,title,body,created_at,stream:streams(name,class:classes(name))")
          .in("stream_id", streamIds).order("created_at", { ascending: false }).limit(4) : Promise.resolve({ data: [] } as any),
        supabase.from("timetable_slots")
          .select("id,day,start_time,end_time,room,subject:subjects_catalog(name),stream:streams(name,class:classes(name))")
          .eq("staff_id", staffId).eq("day", dayName).order("start_time"),
      ]);

      const present = (attToday.data ?? []).filter((a: any) => a.status === "present" || a.status === "late").length;
      setCounts({
        assignmentsDue: assignsDue.count ?? 0,
        ungraded: ungraded.count ?? 0,
        attToday: present,
        attTotal: attToday.data?.length ?? 0,
        learners: learnerCount.count ?? 0,
      });
      setRecentAssignments(recA.data ?? []);
      setRecentSubmissions(recS.data ?? []);
      setAnnouncements(anns.data ?? []);
      setTodaySlots(slots.data ?? []);
    })();
  }, [ctx.loading, ctx.staffId, ctx.schoolId, ctx.streams]);

  if (ctx.loading) {
    return <AppShell role="teacher"><div className="grid h-64 place-items-center text-muted-foreground">Loading…</div></AppShell>;
  }

  if (!ctx.staffId) {
    return (
      <AppShell role="teacher" title="Welcome">
        <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <Sparkles className="mx-auto h-10 w-10 text-primary" />
          <h2 className="mt-3 font-display text-xl font-bold">Your teacher profile is being set up</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            Ask your school admin to add you to the staff directory. Once added and assigned to classes you'll see your dashboard here.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      role="teacher"
      title={`${greet()}, ${ctx.fullName.split(" ")[0] || "Teacher"}`}
      subtitle={ctx.schoolName ?? undefined}
    >
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat icon={Users} k={String(counts.learners)} v="Learners" tint="bg-primary-soft text-primary" />
        <Stat icon={BookOpen} k={String(ctx.streams.length)} v="Classes" tint="bg-sky/15 text-sky" />
        <Stat icon={ClipboardList} k={String(counts.assignmentsDue)} v="Due this week" tint="bg-gold/20 text-gold-foreground" />
        <Stat icon={ClipboardCheck} k={String(counts.ungraded)} v="To grade" tint="bg-primary-soft text-primary" />
        <Stat icon={CalendarCheck}
              k={counts.attTotal ? `${counts.attToday}/${counts.attTotal}` : "—"}
              v="Present today" tint="bg-sky/15 text-sky" />
      </section>

      {ctx.streams[0] ? (
        <section className="mt-4">
          <WeeklySummaryCard kind="class" streamId={ctx.streams[0].id} name={ctx.streams[0].name} />
        </section>
      ) : null}


      <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold">Your workspace</h2>
            <p className="text-xs text-muted-foreground">Everything you need to plan, teach and assess — saved automatically.</p>
          </div>
          <Link to="/teacher/assistant" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gradient-gold px-3 py-1.5 text-xs font-bold text-gold-foreground">
            <Bot className="h-3.5 w-3.5" /> Ask the AI assistant
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Tile to="/teacher/assistant"     icon={Bot}           label="AI Assistant"   tint="bg-gradient-gold text-gold-foreground" />
          <Tile to="/teacher/generator"     icon={Wand2}         label="Lesson Generator" />
          <Tile to="/teacher/exams"         icon={ScrollText}    label="Exam Generator" />
          <Tile to="/teacher/planner"       icon={FileText}      label="Lesson Plans" />
          <Tile to="/teacher/assignments"   icon={ClipboardList} label="Assignments" />
          <Tile to="/teacher/gradebook"     icon={BarChart3}     label="Gradebook" />
          <Tile to="/teacher/reports/cards" icon={ScrollText}    label="Report Cards" />
          <Tile to="/teacher/behaviour"     icon={Heart}         label="Behaviour" />
          <Tile to="/teacher/attendance"    icon={CalendarCheck} label="Attendance" />
          <Tile to="/teacher/timetable"     icon={CalendarDays}  label="Timetable" />
          <Tile to="/teacher/whiteboard"    icon={PenTool}       label="Whiteboard" />
          <Tile to="/teacher/resources"     icon={FolderOpen}    label="Resources" />
          <Tile to="/teacher/messages"      icon={MessageSquare} label="Parent Messaging" />
          <Tile to="/teacher/live"          icon={Video}         label="Live Classes" />
        </div>
      </section>


      <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Today's timetable</h2>
          <span className="text-xs text-muted-foreground">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
        </div>
        {todaySlots.length === 0 ? <EmptyState text="No scheduled lessons today." /> : (
          <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {todaySlots.map((sl) => (
              <li key={sl.id} className="rounded-xl border border-border bg-gradient-african/5 p-3">
                <div className="text-xs font-bold text-primary">{sl.start_time?.slice(0,5)} – {sl.end_time?.slice(0,5)}</div>
                <div className="font-display font-bold">{sl.subject?.name ?? "Lesson"}</div>
                <div className="text-xs text-muted-foreground">{sl.stream?.class?.name} · {sl.stream?.name}{sl.room ? ` · ${sl.room}` : ""}</div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">

        <section className="lg:col-span-2 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Your classes</h2>
            <Link to="/teacher/classes" className="text-sm font-semibold text-primary hover:underline">Manage all</Link>
          </div>
          {ctx.streams.length === 0 ? (
            <EmptyState text="No classes assigned yet. Your school admin assigns classes and subjects to teachers." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {ctx.streams.slice(0, 6).map((s) => {
                const subs = ctx.assignments.filter((a) => a.stream.id === s.id && a.subject).map((a) => a.subject!.name);
                return (
                  <Link key={s.id} to="/teacher/classes" className="rounded-xl border border-border bg-background/60 p-4 hover:border-primary/40">
                    <div className="font-display font-bold">{s.class_name} · {s.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{subs.length ? subs.join(", ") : "Class teacher"}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Announcements</h2>
            <Megaphone className="h-4 w-4 text-muted-foreground" />
          </div>
          {announcements.length === 0 ? (
            <EmptyState text="No announcements yet." />
          ) : (
            <ul className="space-y-3">
              {announcements.map((a) => (
                <li key={a.id} className="rounded-xl border border-border bg-background/60 p-3">
                  <div className="text-sm font-semibold">{a.title}</div>
                  <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{a.body}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">{a.stream?.class?.name} · {a.stream?.name}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Recent assignments</h2>
            <Link to="/teacher/assignments" search={{ open: undefined }} className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
              See all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {recentAssignments.length === 0 ? <EmptyState text="No assignments yet." /> : (
            <ul className="divide-y divide-border">
              {recentAssignments.map((a) => (
                <li key={a.id} className="py-3">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{a.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {a.stream?.class?.name} · {a.stream?.name} · {a.subject?.name}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">{a.due_at ? new Date(a.due_at).toLocaleDateString() : "No due"}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Recent activity</h2>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </div>
          {recentSubmissions.length === 0 ? <EmptyState text="No submissions yet." /> : (
            <ul className="divide-y divide-border">
              {recentSubmissions.map((s) => (
                <li key={s.id} className="py-3">
                  <div className="text-sm">
                    <b>{s.learner?.first_name} {s.learner?.last_name}</b>{" "}
                    <span className="text-muted-foreground">
                      {s.status === "graded" ? "graded" : "submitted"} {s.assignment?.title}
                    </span>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : "—"}
                    {s.score != null ? ` · ${s.score}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="mt-6">
        <AICallout
          description="I can draft report comments, plan tomorrow's lessons, and flag learners who need attention."
          suggestions={[
            "Summarise my day",
            "Which learners need attention?",
            "Draft a class announcement",
            "Write report comments for low scorers",
          ]}
        />
      </div>
    </AppShell>
  );
}

function greet() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function Stat({ icon: Icon, k, v, tint }: { icon: typeof Users; k: string; v: string; tint: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className={`grid h-9 w-9 place-items-center rounded-xl ${tint}`}><Icon className="h-4.5 w-4.5" /></div>
      <div className="mt-2 font-display text-xl font-bold">{k}</div>
      <div className="text-[11px] text-muted-foreground">{v}</div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="grid place-items-center rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{text}</div>;
}

function Tile({ to, icon: Icon, label, tint }: { to: string; icon: typeof Users; label: string; tint?: string }) {
  return (
    <Link to={to} className="group rounded-2xl border border-border bg-background/60 p-4 transition hover:border-primary/40 hover:shadow-soft">
      <div className={`grid h-10 w-10 place-items-center rounded-xl ${tint ?? "bg-primary-soft text-primary"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-3 text-sm font-semibold">{label}</div>
      <div className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary">
        Open <ArrowRight className="h-3 w-3" />
      </div>
    </Link>
  );
}
